import type { IntlShape, MessageDescriptor } from "react-intl";

import type { InvitedModes } from "api/models/draftRequest.model";
import type { AvailableFreightMode } from "api/response/location.response";

export const KNOWN_FREIGHT_API_TYPES = new Set(["air", "ocean", "road", "rail"]);
export const ORDERED_FREIGHT_CATEGORIES = ["air", "ocean", "road", "others"] as const;

export type FreightModeCategory = (typeof ORDERED_FREIGHT_CATEGORIES)[number];

export type ResolvedFreightMode = {
  label: string;
  value: string;
  type?: string;
};

export type FreightModeCategoryCounts = Record<FreightModeCategory, number>;

const CATEGORY_LABEL_DESCRIPTORS: Record<FreightModeCategory, MessageDescriptor> = {
  air: { id: "request-form.service.freightMode.air", defaultMessage: "Air" },
  ocean: { id: "request-form.service.freightMode.ocean", defaultMessage: "Ocean" },
  road: { id: "request-form.service.freightMode.land", defaultMessage: "Land" },
  others: { id: "request-form.service.freightMode.parcel", defaultMessage: "Parcel" },
};

export function apiTypeToCategory(apiType: string | undefined): FreightModeCategory {
  if (apiType === "air") return "air";
  if (apiType === "ocean") return "ocean";
  if (apiType === "road" || apiType === "rail") return "road";
  return "others";
}

export function resolveSelectedFreightModes(
  invitedModes: InvitedModes | undefined,
  availableModes: AvailableFreightMode[],
): ResolvedFreightMode[] {
  if (!invitedModes || !availableModes.length) return [];

  const resolved: ResolvedFreightMode[] = [];

  availableModes.forEach((mode) => {
    if (mode.value === "road_freight") {
      if (invitedModes.road_freight === true) {
        const optionValue = invitedModes.road_freight_option;
        if (optionValue && mode.options) {
          const opt = mode.options.find((o) => o.value === optionValue);
          if (opt) {
            resolved.push({ label: `${mode.label}: ${opt.label}`, value: opt.value, type: mode.type });
            return;
          }
        }
        resolved.push({ label: mode.label, value: mode.value, type: mode.type });
      }
    } else if (mode.value.startsWith("custom_")) {
      // handled via custom array below
    } else if (invitedModes[mode.value] === true) {
      resolved.push({ label: mode.label, value: mode.value, type: mode.type });
    }
  });

  const customItems = invitedModes.custom;
  if (Array.isArray(customItems)) {
    customItems.forEach((item) => {
      if (item.value) {
        const modeKey = `custom_${item.key}`;
        const mode = availableModes.find((m) => m.value === modeKey);
        if (mode) {
          resolved.push({ label: mode.label, value: mode.value, type: mode.type });
        }
      }
    });
  }

  return resolved;
}

export function countSelectedFreightModesByCategory(
  selectedModes: ResolvedFreightMode[],
): FreightModeCategoryCounts {
  const counts: FreightModeCategoryCounts = { air: 0, ocean: 0, road: 0, others: 0 };

  selectedModes.forEach((mode) => {
    const category = apiTypeToCategory(mode.type);
    counts[category] += 1;
  });

  return counts;
}

export function formatFreightModeCategorySummary(
  counts: FreightModeCategoryCounts,
  intl: IntlShape,
): string {
  return ORDERED_FREIGHT_CATEGORIES.filter((category) => counts[category] > 0)
    .map((category) => {
      const label = intl.formatMessage(CATEGORY_LABEL_DESCRIPTORS[category]).toLowerCase();
      return `${counts[category]} ${label}`;
    })
    .join(", ");
}
